import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import instructionService, { Instruction } from '../../services/instructionService';
import Button from '../../components/common/Button/Button';
import styles from './InstructionManagement.module.scss';
import { BACKEND_BASE_URL } from '../../utils/constants';

const InstructionManagement: React.FC = () => {
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<Instruction | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    is_active: true,
    image: null as File | null,
  });

  useEffect(() => {
    loadInstructions();
  }, []);

  const loadInstructions = async () => {
    try {
      setLoading(true);
      const data = await instructionService.getAll();
      console.log(data);
      setInstructions(data || []); // Ensure we always set an array
    } catch (error) {
      console.error('Failed to load instructions:', error);
      toast.error('Failed to load instructions');
      setInstructions([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    try {
      if (editingInstruction) {
        await instructionService.update(editingInstruction.id, formData);
        toast.success('Instruction updated successfully');
      } else {
        await instructionService.create(formData);
        toast.success('Instruction created successfully');
      }
      
      setShowForm(false);
      setEditingInstruction(null);
      setFormData({ title: '', content: '', is_active: true, image: null });
      loadInstructions();
    } catch (error) {
      console.error('Failed to save instruction:', error);
      toast.error('Failed to save instruction');
    }
  };

  const handleEdit = (instruction: Instruction) => {
    setEditingInstruction(instruction);
    setFormData({
      title: instruction.title,
      content: instruction.content,
      is_active: instruction.is_active,
      image: null, // Clear image when editing
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this instruction?')) {
      return;
    }

    try {
      await instructionService.delete(id);
      toast.success('Instruction deleted successfully');
      loadInstructions();
    } catch (error) {
      console.error('Failed to delete instruction:', error);
      toast.error('Failed to delete instruction');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingInstruction(null);
    setFormData({ title: '', content: '', is_active: true, image: null });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading instructions...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Instructions</h2>
        <Button
          variant="primary"
          onClick={() => setShowForm(true)}
          disabled={showForm}
        >
          Add New Instruction
        </Button>
      </div>

      {showForm && (
        <div className={styles.formContainer}>
          <h3>{editingInstruction ? 'Edit Instruction' : 'Add New Instruction'}</h3>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter instruction title"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="content">Content *</label>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter instruction content"
                rows={6}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active (visible to employees)
              </label>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="image">Image (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={e => setFormData({ ...formData, image: e.target.files?.[0] || null })}
              />
              {formData.image && (
                <div className={styles.imagePreview}>
                  <img src={URL.createObjectURL(formData.image)} alt="Preview" />
                </div>
              )}
            </div>

            <div className={styles.formActions}>
              <Button type="submit" variant="primary">
                {editingInstruction ? 'Update' : 'Create'} Instruction
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className={styles.instructionsList}>
        <h3>All Instructions</h3>
        {instructions.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No instructions found. Create your first instruction to get started.</p>
          </div>
        ) : (
          <div className={styles.instructionsGrid}>
            {instructions.map((instruction) => (
              <div key={instruction.id} className={styles.instructionCard}>
                <div className={styles.instructionHeader}>
                  <h4>{instruction.title}</h4>
                  <div className={styles.status}>
                    <span className={`${styles.statusBadge} ${instruction.is_active ? styles.active : styles.inactive}`}>
                      {instruction.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                
                <div className={styles.instructionContent}>
                  <p>{instruction.content}</p>
                  {instruction.image_url && (
                    <img src={BACKEND_BASE_URL + instruction.image_url} alt="Instruction" style={{ maxWidth: 200 }} />
                  )}
                </div>
                
                <div className={styles.instructionMeta}>
                  <span>Created: {new Date(instruction.created_at).toLocaleDateString()}</span>
                  {instruction.updated_at !== instruction.created_at && (
                    <span>Updated: {new Date(instruction.updated_at).toLocaleDateString()}</span>
                  )}
                </div>
                
                <div className={styles.instructionActions}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleEdit(instruction)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(instruction.id)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructionManagement; 